/**
 * Admin Users Screen - MOBILE ONLY
 * 
 * View and manage all users.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, Redirect } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { authApi } from '../../../shared/utils/api';
import { logger } from '../../../shared/utils/logger';
import { User } from '../../../shared/types/auth';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard, SearchInput } from '../../../components/v2/mobile';

export default function AdminUsersScreen() {
  const router = useRouter();
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.role === 'admin';

  const [users, setUsers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await authApi.getAllUsers();
      if (response.success) {
        setUsers(response.users || []);
      }
    } catch (err) {
      logger.error('Failed to fetch users', 'admin', String(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    fetchUsers();
  }, [fetchUsers]);

  const handleBack = useCallback(() => router.back(), [router]);

  // Filter users by search
  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.display_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Redirect non-admins
  if (!isAdmin) {
    return <Redirect href="/v2/home" />;
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={color.primary} />}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>All Users</Text>
          <View style={styles.spacer} />
        </View>

        {/* SEARCH */}
        <View style={styles.section}>
          <SearchInput value={search} onChangeText={setSearch} placeholder="Search users..." />
        </View>

        {/* USER LIST */}
        <View style={styles.section}>
          {loading ? (
            <View style={styles.loadingState}>
              <ActivityIndicator size="large" color={color.primary} />
            </View>
          ) : filteredUsers.length === 0 ? (
            <ListCard>
              <View style={styles.emptyState}>
                <Ionicons name="people-outline" size={icon.xxl} color={color.textMuted} />
                <Text style={styles.emptyText}>{search ? 'No users found' : 'No users yet'}</Text>
              </View>
            </ListCard>
          ) : (
            <ListCard>
              {filteredUsers.map((user, index) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isCurrentUser={user.id === currentUser?.id}
                  hideBorder={index === filteredUsers.length - 1}
                />
              ))}
            </ListCard>
          )}
        </View>

        <View style={styles.countContainer}>
          <Text style={styles.countText}>{users.length} total users</Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// USER ROW
// =============================================================================

function UserRow({ user, isCurrentUser, hideBorder }: { user: User; isCurrentUser: boolean; hideBorder: boolean }) {
  const displayName = user.display_name || user.email.split('@')[0];
  const roleColor = user.role === 'admin' ? color.primary : color.textSecondary;

  return (
    <View style={[styles.userRow, !hideBorder && styles.userRowBorder]}>
      <View style={styles.avatar}>
        <Text style={styles.avatarText}>{displayName.charAt(0).toUpperCase()}</Text>
      </View>
      <View style={styles.userContent}>
        <View style={styles.userNameRow}>
          <Text style={styles.userName} numberOfLines={1}>{displayName}</Text>
          {isCurrentUser && <Text style={styles.youBadge}>You</Text>}
        </View>
        <Text style={styles.userEmail} numberOfLines={1}>{user.email}</Text>
      </View>
      <View style={[styles.roleBadge, { backgroundColor: roleColor + '15' }]}>
        <Text style={[styles.roleText, { color: roleColor }]}>{user.role || 'user'}</Text>
      </View>
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  loadingState: { paddingVertical: space.xxl, alignItems: 'center' },
  emptyState: { paddingVertical: space.xxl, alignItems: 'center', gap: space.md },
  emptyText: { fontSize: text.body.fontSize, color: color.textMuted },
  countContainer: { alignItems: 'center', paddingVertical: space.md },
  countText: { fontSize: text.caption.fontSize, color: color.textMuted },
  userRow: { flexDirection: 'row', alignItems: 'center', padding: space.md, gap: space.sm },
  userRowBorder: { borderBottomWidth: border.width.thin, borderBottomColor: color.divider },
  avatar: { width: 40, height: 40, borderRadius: 20, backgroundColor: color.primaryLight, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: text.body.fontSize, fontWeight: '600', color: color.primary },
  userContent: { flex: 1 },
  userNameRow: { flexDirection: 'row', alignItems: 'center', gap: space.xs },
  userName: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  youBadge: { fontSize: text.caption.fontSize - 1, fontWeight: '500', color: color.textMuted, backgroundColor: color.backgroundSecondary, paddingHorizontal: space.xs, borderRadius: radius.sm },
  userEmail: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: 2 },
  roleBadge: { paddingHorizontal: space.sm, paddingVertical: space.xs - 1, borderRadius: radius.full },
  roleText: { fontSize: text.caption.fontSize - 1, fontWeight: '600', textTransform: 'capitalize' },
});

